// Edit operations exposed via React context. Consumers call
// `update(recipe)` with an immer producer; useSongEdit computes the
// next Song, pushes to history, regenerates code, and emits it upward.

import { createContext, useContext } from 'react';
import type { Song, SourceRange } from './types';

export type EditRecipe = (draft: Song) => void;

export type EditOps = {
  update: (recipe: EditRecipe) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** Click a block → ask the editor to select the corresponding source range. */
  selectSourceRange: (range: SourceRange) => void;
  /** Request that a block (by id) be scrolled into view on the next frame.
   *  Used after adds so users can see the newly-created block without
   *  having to hunt for it in a long list. */
  revealBlock: (id: string) => void;
};

const NOOP_OPS: EditOps = {
  update: () => {},
  undo: () => {},
  redo: () => {},
  canUndo: false,
  canRedo: false,
  selectSourceRange: () => {},
  revealBlock: () => {},
};

export const EditContext = createContext<EditOps>(NOOP_OPS);

export function useEditContext(): EditOps {
  return useContext(EditContext);
}
